import * as SelectPrimitive from "@radix-ui/react-select";
import { Check as CheckIcon, ChevronDown as ChevronDownIcon } from "lucide-react";
import { forwardRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex w-full min-w-0 items-center gap-2 rounded-md border border-border-strong bg-surface px-[0.65rem] py-[0.4rem] text-start text-[0.9rem] text-foreground transition-[border-color,box-shadow] duration-150 ease-in-out focus:border-primary focus:shadow-[0_0_0_3px_var(--color-ring)] focus:outline-none data-[placeholder]:text-subtle",
      className,
    )}
    {...props}
  >
    <span className="min-w-0 flex-1 truncate text-start">{children}</span>
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="ms-auto shrink-0 opacity-60" size={16} aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    viewportClassName?: string;
    collisionPadding?: number;
  }
>(({ className, children, position = "popper", viewportClassName, collisionPadding, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "z-[var(--z-popover)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-border bg-surface-elevated text-start shadow-md",
        className,
      )}
      position={position}
      collisionPadding={collisionPadding}
      {...props}
    >
      <SelectPrimitive.Viewport className={cn("p-1", viewportClassName)}>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { detail?: ReactNode }
>(({ className, children, detail, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "group flex w-full cursor-pointer items-center gap-2 rounded-sm px-[0.6rem] py-[0.4rem] text-start text-[0.9rem] outline-none data-[highlighted]:bg-surface-muted",
      className,
    )}
    {...props}
  >
    <span className="grid min-w-0 flex-1 gap-0.5 text-start">
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {detail}
    </span>
    <span className="inline-flex size-4 shrink-0 items-center justify-center opacity-0 group-data-[state=checked]:opacity-100">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon size={14} />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export interface SelectOption {
  value: string;
  label: ReactNode;
  /** Dropdown-only secondary content. Omitted from the closed trigger. */
  detail?: ReactNode;
}

export function SimpleSelect({
  value,
  onValueChange,
  options,
  placeholder,
  ariaLabel,
  triggerClassName,
  triggerChildren,
  itemClassName,
  contentClassName,
  viewportClassName,
  collisionPadding,
  disabled,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
  triggerClassName?: string;
  /** When set, replaces the default SelectValue + chevron content. */
  triggerChildren?: ReactNode;
  itemClassName?: string;
  contentClassName?: string;
  /** Opt-in scroll cap. Omitted selects keep the default viewport. */
  viewportClassName?: string;
  /** Opt-in. Omitted selects keep Radix's default collision padding. */
  collisionPadding?: number;
  disabled?: boolean;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const dir = i18n.dir() === "rtl" ? "rtl" : "ltr";

  return (
    <Select dir={dir} value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger aria-label={ariaLabel} className={triggerClassName}>
        {triggerChildren ?? <SelectValue placeholder={placeholder} />}
      </SelectTrigger>
      <SelectContent
        className={contentClassName}
        viewportClassName={viewportClassName}
        collisionPadding={collisionPadding}
      >
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className={itemClassName}
            detail={option.detail}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
